import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomCalendar } from "../../../../components/CustomCalendar/CustomCalendar";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { WeekStartDaySelector } from "../../../../components/WeekStartDaySelector/WeekStartDaySelector";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import type { Nurse, TransformedShift } from "../../../../utils/models";
import { ShiftCard } from "../../../BedsideHome/components/ShiftCard";
import { SkeletonWithRandomWidths } from "../util/constants";
import { useNurseScheduleShifts } from "./useNurseScheduleShifts";

type NurseSchedulePreviewProps = {
	nurse: Nurse;
};

export const NurseSchedulePreview: FC<NurseSchedulePreviewProps> = ({ nurse }) => {
	const { t } = useTranslation();
	const nurseId = nurse.id;
	const { weeks, shifts: nurseShifts, isLoading: isLoadingSchedule, isError: isScheduleError } = useNurseScheduleShifts(nurse);
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const location = useLocation();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
	const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(true);

	const selectedShifts = useMemo<TransformedShift[]>(() => {
		const dateKey = selectedDate.toFormat("yyyy-MM-dd");
		return nurseShifts.filter((s) => s.id != null && s.date.toFormat("yyyy-MM-dd") === dateKey);
	}, [nurseShifts, selectedDate]);

	useEffect(() => {
		if (selectedShifts.length === 0) return;
		track("staff_profile_shift_selected", {
			viewing_nurse_id: nurseId,
			viewer_role: userInfo.role,
			date: selectedDate.toFormat("yyyy-MM-dd"),
			shift_count: selectedShifts.length,
			shift_ids: selectedShifts.map((s) => s.id).join(","),
		});
	}, [selectedShifts, selectedDate, nurseId, userInfo.role, track]);

	const handleShiftClick = (shift: TransformedShift): void => {
		if (!shift.id) return;
		track("staff_profile_shift_edit_clicked", {
			viewing_nurse_id: nurseId,
			viewer_role: userInfo.role,
			shift_id: shift.id,
			date: shift.date.toFormat("yyyy-MM-dd"),
		});
		const returnTo = `${location.pathname}${location.search}`;
		navigate(`/${chosenDepartment.department_id}/shift/${shift.id}?returnTo=${encodeURIComponent(returnTo)}`);
	};

	const handleDateChange = (newDate: DateTime): void => {
		track("staff_profile_calendar_date_changed", {
			viewing_nurse_id: nurseId,
			viewer_role: userInfo.role,
			previous_date: selectedDate.toFormat("yyyy-MM-dd"),
			new_date: newDate.toFormat("yyyy-MM-dd"),
		});
		setSelectedDate(newDate);
	};

	if (isLoadingSchedule) {
		return <SkeletonWithRandomWidths card />;
	}
	// The hook has already told the user about a failure.
	if (!weeks || (weeks.length === 0 && isScheduleError)) {
		return <EmptyState title={t("error.nurse.schedule.data")} dataTestId="profile-schedule-preview-error" />;
	}
	if (weeks.length === 0) {
		return <EmptyState title={t("profile.schedule.empty")} dataTestId="profile-schedule-preview-empty" />;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2, p: 1 }}>
			{isDynamicWeekStartEnabled && (
				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<WeekStartDaySelector source="profile" />
				</Box>
			)}
			<CustomCalendar
				shifts={nurseShifts}
				value={selectedDate}
				onChange={handleDateChange}
				isOpen={isCalendarOpen}
				onOpenChange={setIsCalendarOpen}
				showOneWeek={false}
				allowWeeksExpansion={true}
			/>
			{isCalendarOpen && selectedShifts.length > 0 && (
				<ShiftCard
					dateKey={selectedDate.toFormat("yyyy-MM-dd")}
					currentDate={selectedDate}
					shifts={selectedShifts}
					onItemClick={handleShiftClick}
					variant="simple"
					readOnly
				/>
			)}
		</Box>
	);
};
